import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CardGiftcard as CardGiftcardIcon,
  AccessTime as AccessTimeIcon,
  ShoppingBag as ShoppingBagIcon,
  LocalOffer as LocalOfferIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { evouchersApi } from '../api/evouchers';
import EvoucherCheckout from '../../components/evouchers/evoucherCheckout';
import { currencyconverter } from '../../utils/dateFormatter';
import Seo from '../components/Seo';

const EVoucher = () => {
  const navigate = useNavigate();
  const evoucherEnabled = useSelector((state) => state.configs?.evoucherEnabled);
  const businessName = useSelector((state) => state.configs?.businessName);
  const { isLoggedIn } = useSelector((store) => store?.user);

  useEffect(() => {
    if (evoucherEnabled === false) navigate('/', { replace: true });
  }, [evoucherEnabled, navigate]);

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [checkout, setCheckout] = useState(false);
  const [voucherData, setVoucherData] = useState(null);

  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedAmount, setSelectedAmount] = useState(null);

  const loadCampaigns = () => {
    setLoading(true);
    setLoadError(null);
    evouchersApi
      .getActiveCampaigns()
      .then((data) => {
        setCampaigns(data);
        const firstWithAmounts = data.find((item) => item?.amounts?.length);
        if (firstWithAmounts) {
          setSelectedCampaign(firstWithAmounts);
          setSelectedAmount(firstWithAmounts.amounts[0]);
        }
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectAmount = (campaign, amount) => {
    setSelectedCampaign(campaign);
    setSelectedAmount(amount);
  };

  const handlePurchase = () => {
    if (!selectedCampaign || !selectedAmount) return;
    if (!isLoggedIn) {
      navigate('/login', { state: { previousPath: '/evoucher' } });
      return;
    }
    setVoucherData({ voucherDetails: selectedCampaign, selectedVoucher: selectedAmount });
    setCheckout(true);
    window.scrollTo(0, 0);
  };

  // Geometric background style
  const backgroundStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      linear-gradient(135deg, color-mix(in srgb, var(--primary) 3%, transparent) 0%, color-mix(in srgb, var(--primary) 1%, transparent) 100%),
      repeating-linear-gradient(45deg, transparent, transparent 35px, color-mix(in srgb, var(--primary) 2%, transparent) 35px, color-mix(in srgb, var(--primary) 2%, transparent) 70px),
      repeating-linear-gradient(-45deg, transparent, transparent 35px, color-mix(in srgb, var(--primary) 1%, transparent) 35px, color-mix(in srgb, var(--primary) 1%, transparent) 70px)
    `,
    zIndex: -1,
  };

  return (
    <>
      <Seo title="Gift Vouchers" description="Buy digital gift vouchers." />
      <Box sx={backgroundStyle} />
      <Box sx={{ minHeight: '100vh', py: 5, position: 'relative' }}>
        <Container maxWidth="lg">
          {/* Header */}
          <Box sx={{ mb: 5, textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
              E-Voucher Gift
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400 }}>
              Give the gift of choice with {businessName ? `${businessName} ` : ''}e-vouchers
            </Typography>
          </Box>

          {checkout ? (
            <EvoucherCheckout
              voucherData={voucherData}
              setVoucherData={setVoucherData}
              setCheckout={setCheckout}
            />
          ) : loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress sx={{ color: 'var(--primary)' }} />
            </Box>
          ) : loadError ? (
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <Alert severity="error" sx={{ display: 'inline-flex', mb: 3 }}>
                {loadError}
              </Alert>
              <Box>
                <Button variant="contained" onClick={loadCampaigns} sx={{ backgroundColor: 'var(--primary)' }}>
                  Retry
                </Button>
              </Box>
            </Box>
          ) : campaigns.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 10 }}>
              <CardGiftcardIcon sx={{ fontSize: 60, color: 'var(--primary)', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No gift vouchers available right now.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={4}>
              {/* Left Side - Gift Card Preview */}
              <Grid item xs={12} md={5}>
                <Box sx={{ position: 'sticky', top: 100 }}>
                  <Box
                    sx={{
                      background: '#ffffff',
                      borderRadius: '12px',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                      mb: 3,
                      border: '1px solid rgba(0,0,0,0.08)',
                    }}
                  >
                    {/* Decorative Header */}
                    <Box
                      sx={{
                        height: '120px',
                        background: 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                      }}
                    >
                      <CardGiftcardIcon sx={{ fontSize: 36, mb: 1, opacity: 0.9 }} />
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          letterSpacing: 3,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          opacity: 0.9,
                        }}
                      >
                        Gift Voucher
                      </Typography>
                    </Box>

                    {/* Main Content */}
                    <Box sx={{ p: 4, backgroundColor: 'white' }}>
                      <Box sx={{ textAlign: 'center', mb: 1 }}>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            color: '#999',
                            letterSpacing: 2,
                            mb: 1,
                            textTransform: 'uppercase',
                          }}
                        >
                          Value
                        </Typography>
                        <Box
                          sx={{
                            display: 'inline-block',
                            padding: '12px 32px',
                            background:
                              'linear-gradient(135deg, color-mix(in srgb, var(--primary) 5%, transparent) 0%, color-mix(in srgb, var(--primary) 2%, transparent) 100%)',
                            borderRadius: '8px',
                            border: '2px solid var(--primary)',
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: { xs: '2rem', sm: '2.5rem' },
                              fontWeight: 700,
                              color: 'var(--primary)',
                              letterSpacing: '-1px',
                            }}
                          >
                            {selectedAmount ? currencyconverter(selectedAmount.voucherAmount) : '-'}
                          </Typography>
                        </Box>
                      </Box>
                      {selectedCampaign?.name && (
                        <Typography sx={{ textAlign: 'center', mt: 2, color: 'text.secondary', fontSize: '0.9rem' }}>
                          {selectedCampaign.name}
                        </Typography>
                      )}
                    </Box>

                    <Box
                      sx={{
                        height: '6px',
                        background: 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
                      }}
                    />
                  </Box>

                  {/* Features */}
                  <Paper sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Why Choose Our Gift Vouchers?
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {[
                        { icon: <AccessTimeIcon />, text: 'Instant email delivery' },
                        { icon: <ShoppingBagIcon />, text: 'Use on any product' },
                        { icon: <LocalOfferIcon />, text: 'No expiry fees' },
                        { icon: <SecurityIcon />, text: 'Secure & protected' },
                      ].map((item, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: '50%',
                              bgcolor: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'var(--primary)',
                            }}
                          >
                            {React.cloneElement(item.icon, { fontSize: 'small' })}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {item.text}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Box>
              </Grid>

              {/* Right Side - Campaign / Amount Picker */}
              <Grid item xs={12} md={7}>
                <Paper sx={{ p: 4, borderRadius: 2 }}>
                  {campaigns.map((campaign) => (
                    <Box key={campaign._id} sx={{ mb: 4 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ mb: 2, fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}
                      >
                        {(campaign.name || 'SELECT AMOUNT').toUpperCase()}
                      </Typography>
                      <Grid container spacing={1.5}>
                        {campaign.amounts?.map((amount) => {
                          const isSelected = selectedAmount?._id === amount._id;
                          return (
                            <Grid item xs={6} sm={3} key={amount._id}>
                              <Button
                                fullWidth
                                onClick={() => handleSelectAmount(campaign, amount)}
                                variant={isSelected ? 'contained' : 'outlined'}
                                sx={{
                                  py: 1.5,
                                  borderColor: isSelected ? 'var(--primary)' : '#e0e0e0',
                                  backgroundColor: isSelected ? 'var(--primary)' : 'transparent',
                                  color: isSelected ? 'white' : 'text.primary',
                                  borderWidth: 2,
                                  fontWeight: 600,
                                  '&:hover': {
                                    borderColor: 'var(--primary)',
                                    backgroundColor: isSelected
                                      ? 'var(--button-hover)'
                                      : 'color-mix(in srgb, var(--primary) 5%, transparent)',
                                    borderWidth: 2,
                                  },
                                }}
                              >
                                {currencyconverter(amount.voucherAmount)}
                              </Button>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  ))}

                  <Alert severity="info" sx={{ borderRadius: 2, mb: 4 }}>
                    <Typography variant="body2">
                      You can purchase one voucher at a time. To buy multiple vouchers, please complete separate
                      transactions.
                    </Typography>
                  </Alert>

                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={!selectedAmount}
                    onClick={handlePurchase}
                    sx={{
                      py: 2,
                      background: 'linear-gradient(135deg, var(--primary) 0%, var(--button-hover) 100%)',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      boxShadow: '0 4px 15px color-mix(in srgb, var(--primary) 40%, transparent)',
                      borderRadius: '6px',
                      '&:hover': {
                        background: 'linear-gradient(135deg, var(--button-hover) 0%, var(--primary) 100%)',
                        boxShadow: '0 6px 20px color-mix(in srgb, var(--primary) 50%, transparent)',
                      },
                      '&:disabled': {
                        background: '#e0e0e0',
                      },
                    }}
                  >
                    Buy Gift Voucher
                  </Button>

                  <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 2, color: 'text.secondary' }}>
                    By purchasing you agree to our terms and conditions. Gift vouchers are non-refundable.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>
    </>
  );
};

export default EVoucher;
